import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';

import { TableCell } from 'common/components/Table';
import { CERTIFICATE_COLUMN_STAGGER_MS } from 'skill/constants/skillReportMatrix';
import { ISkillCertificateColumn } from 'skill/hooks/useSkillReportColumnExpansion';

interface IProps {
    certificate?: ISkillCertificateColumn;
    isExpanded: boolean;
    isLast: boolean;
    navHeights: string;
    isLoading?: boolean;
    columnIndex?: number;
}

export const SkillReportMatrixCertificateHeaderCell = ({
    certificate,
    isExpanded,
    isLast,
    navHeights,
    isLoading,
    columnIndex = 0,
}: IProps) => {
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        const id = requestAnimationFrame(() => setIsMounted(true));

        return () => cancelAnimationFrame(id);
    }, []);

    const visible = isMounted && isExpanded;
    // Stagger expand to ripple columns left-to-right; collapse with no delay
    // so it stays snappy.
    const transitionDelay = visible
        ? `${columnIndex * CERTIFICATE_COLUMN_STAGGER_MS}ms`
        : '0ms';

    return (
        <TableCell
            sx={{
                top: navHeights,
                zIndex: 4,
                p: 0,
                verticalAlign: 'bottom',
                whiteSpace: 'nowrap',
                width: visible ? 44 : 0,
                minWidth: visible ? 44 : 0,
                maxWidth: visible ? 44 : 0,
                bgcolor: visible ? 'grey.100' : 'transparent',
                borderRightStyle: 'solid',
                borderRightWidth: visible && isLast ? '1px' : 0,
                borderRightColor: 'divider',
                borderBottomColor: visible ? 'divider' : 'transparent',
                transition:
                    'width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease, background-color 0.3s ease, border-right-width 0.3s ease, border-color 0.3s ease',
                transitionDelay,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    minWidth: visible ? 44 : 0,
                    transition: 'min-width 0.3s ease',
                    transitionDelay,
                }}
            >
                <Box
                    sx={{
                        overflow: 'hidden',
                        flexShrink: 0,
                        width: visible ? 44 : 0,
                        opacity: visible ? 1 : 0,
                        transition: 'width 0.3s ease, opacity 0.3s ease',
                        transitionDelay,
                    }}
                >
                    {certificate ? (
                        <Tooltip title={certificate.name}>
                            <Box
                                sx={{
                                    pt: 2,
                                    maxHeight: '150px',
                                    margin: 'auto',
                                    writingMode: 'vertical-lr',
                                    transform: 'rotate(180deg)',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                }}
                            >
                                {certificate.name}
                            </Box>
                        </Tooltip>
                    ) : isLoading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 1,
                            }}
                        >
                            <CircularProgress size={28} />
                        </Box>
                    ) : null}
                </Box>
            </Box>
        </TableCell>
    );
};
